// Orchestration de `create-wp-reactor --provision`.
//
// Une seule commande, de zéro à une infra qui tourne. L'ordre des étapes n'est
// pas cosmétique : il dénoue un cycle. Coolify a besoin d'images sur GHCR, ces
// images viennent des GitHub Actions, et les Actions ont besoin des UUID Coolify
// pour déclencher le déploiement.
//
// La sortie du cycle : créer les ressources Coolify SANS déployer
// (`instant_deploy: false`), poser les `COOLIFY_*_UUID`, puis pousser. Le
// premier run de la CI construit les images ET déclenche le déploiement via le
// `curl /api/v1/deploy` déjà présent dans `build-and-deploy-image.yml`. Aucun
// polling, aucune seconde commande.

import { execFileSync } from "node:child_process"
import { randomUUID } from "node:crypto"
import { existsSync, readFileSync, rmSync, writeFileSync } from "node:fs"
import { tmpdir } from "node:os"
import { join, resolve } from "node:path"
import { scaffold, toProjectSlug } from "../index"
import { CloudflareClient } from "./cloudflare"
import { CoolifyClient, internalHostFor, webappTraefikLabels } from "./coolify"
import { GitHubClient, pushInitialCommit } from "./github"
import { type HttpCall, Requester } from "./http"
import {
	type GeneratedSecrets,
	generateSecrets,
	redact,
	secretValues,
} from "./secrets"

export interface ProvisionOptions {
	projectName: string
	targetDir: string
	namespace?: string
	/** Domaine du front, ex. `acme.example.com`. WordPress ira sur `wp.<domaine>`. */
	domain: string
	/** Propriétaire GitHub du repo client (compte perso ou organisation). */
	githubOwner: string
	/** Nom du repo. Défaut : le slug du projet. */
	repoName?: string
	/** Serveur Coolify : UUID ou nom affiché. */
	coolifyServer: string
	/**
	 * Cible IPv4 des enregistrements DNS. Défaut : l'IP remontée par Coolify.
	 *
	 * Le serveur d'une instance auto-hébergée — celui que Coolify nomme
	 * `localhost` — rapporte `host.docker.internal`, qui décrit la façon dont
	 * Coolify le joint, pas la façon dont Internet le joint.
	 */
	serverIp?: string
	/**
	 * Environnement provisionné. Décide de la branche poussée, de l'environment
	 * Coolify, du préfixe des ressources et de l'environment GitHub où
	 * atterrissent les valeurs qui lui sont propres.
	 */
	targetEnv?: "production" | "staging"
	/**
	 * Premier run du projet, quel que soit son environnement : lui seul
	 * scaffolde, crée le repo GitHub et le projet Coolify. Les suivants s'y
	 * greffent et n'ajoutent que leur environnement.
	 *
	 * Le mode est explicite plutôt que déduit de l'état distant : c'est ce qui
	 * permet aux garde-fous de rester utiles dans les deux sens — amorcer sur un
	 * projet existant est une erreur, se greffer sur du vide aussi.
	 */
	bootstrap?: boolean
	/** Host WordPress. Défaut : `wp.<domaine>`. */
	wpHost?: string
	dryRun?: boolean
	/** Tokens — lus depuis l'environnement par la CLI, jamais depuis argv. */
	tokens: ProvisionTokens
	log?: (line: string) => void
	fetchImpl?: typeof fetch
}

export interface ProvisionTokens {
	github: string
	coolifyUrl: string
	coolifyToken: string
	/** PAT `read:packages` utilisé par la CI pour installer les `@wp-reactor/*`. */
	nodeAuthToken: string
	cloudflareToken?: string
	cloudflareZoneId?: string
}

export interface ProvisionResult {
	slug: string
	targetDir: string
	appHost: string
	wpHost: string
	repoUrl: string
	actionsUrl: string
	/**
	 * Mot de passe du compte admin WordPress. Il n'est stocké nulle part ailleurs
	 * que dans l'env Coolify : c'est le seul endroit d'où l'opérateur peut le
	 * récupérer, d'où sa présence dans le résultat.
	 */
	wpAdminPassword: string
	coolify: {
		projectUuid: string
		environmentUuid: string
		mariadbUuid: string
		redisUuid: string
		wordpressUuid: string
		webappUuid: string
	}
	/** Journal des appels HTTP, dans l'ordre. Alimente le `--dry-run`. */
	calls: HttpCall[]
}

const REQUIRED_TOKENS: [keyof ProvisionTokens, string][] = [
	["github", "GITHUB_TOKEN"],
	["coolifyUrl", "COOLIFY_URL"],
	["coolifyToken", "COOLIFY_TOKEN"],
	["nodeAuthToken", "NODE_AUTH_TOKEN"],
]

function isIPv4(value: string): boolean {
	const parts = value.split(".")
	return (
		parts.length === 4 &&
		parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)
	)
}

/**
 * Contrôles faisables sans réseau, avant toute création.
 *
 * Le provisioning n'est pas transactionnel : échouer à mi-parcours laisse un
 * projet Coolify et un repo GitHub orphelins. Tout ce qui peut être refusé
 * gratuitement doit l'être ici.
 */
export function preflight(opts: ProvisionOptions): string[] {
	const errors: string[] = []

	for (const [key, envName] of REQUIRED_TOKENS) {
		if (!opts.tokens[key]) {
			errors.push(`Variable d'environnement manquante : ${envName}`)
		}
	}

	const hasCfToken = Boolean(opts.tokens.cloudflareToken)
	const hasCfZone = Boolean(opts.tokens.cloudflareZoneId)
	if (hasCfToken !== hasCfZone) {
		errors.push(
			"CF_API_TOKEN et CF_ZONE_ID vont par paire : fournis les deux (DNS + purge du cache), ou aucun des deux.",
		)
	}

	if (!/^[a-z0-9]([a-z0-9-]*\.)+[a-z]{2,}$/i.test(opts.domain)) {
		errors.push(
			`--domain attend un domaine complet sans schéma (ex. acme.example.com), reçu : "${opts.domain}"`,
		)
	}

	if (!opts.githubOwner) errors.push("--gh-owner est requis")
	if (!opts.coolifyServer) errors.push("--coolify-server est requis")

	if (opts.serverIp && !isIPv4(opts.serverIp)) {
		errors.push(`--server-ip attend une IPv4, reçu : "${opts.serverIp}"`)
	}

	// La règle du dossier suit le MODE, pas l'environnement : l'amorçage le crée
	// et refuse donc d'écraser, une greffe ne scaffolde rien et lit le compose du
	// checkout — il lui faut le dossier, sans quoi l'étape du Compose échouerait
	// à mi-parcours.
	if (opts.bootstrap) {
		if (existsSync(opts.targetDir)) {
			errors.push(
				`Le dossier cible existe déjà : ${opts.targetDir}. --provision refuse d'écrire par-dessus un repo existant.`,
			)
		}
	} else if (!existsSync(opts.targetDir)) {
		errors.push(
			`Le dossier ${opts.targetDir} est introuvable. Sans WPR_BOOTSTRAP le provisioning ne ` +
				"scaffolde pas : fais pointer WPR_DIR sur le checkout du repo client, ou " +
				"passe WPR_BOOTSTRAP=true s'il s'agit du premier run.",
		)
	}

	return errors
}

/**
 * Vérifications distantes, avant la première écriture.
 *
 * `preflight()` ne coûte rien mais ne voit rien : il ignore l'état des trois
 * API. Or les échecs les plus chers — jeton sans la bonne portée, repo ou projet
 * laissé par un run précédent — ne se révélaient qu'une fois le repo GitHub
 * créé, imposant un nettoyage manuel avant chaque relance. Ces sondes sont
 * toutes en lecture seule, et leurs erreurs sont cumulées pour ne pas les
 * découvrir une par une.
 */
export async function preflightRemote(input: {
	github: GitHubClient
	coolify: CoolifyClient
	cloudflare?: CloudflareClient
	owner: string
	repoName: string
	projectName: string
	coolifyServer: string
	bootstrap: boolean
}): Promise<string[]> {
	// Les mêmes sondes, lues dans un sens ou dans l'autre selon le MODE :
	// l'amorçage crée le repo et le projet — leur présence est donc une erreur ;
	// une greffe s'y ajoute — leur absence en est une.
	const bootstrap = input.bootstrap
	const errors: string[] = []
	const message = (error: unknown) =>
		error instanceof Error ? error.message : String(error)

	try {
		const scopes = await input.github.tokenScopes()
		// `undefined` = jeton fine-grained, dont les permissions ne sont pas
		// lisibles par en-tête : on ne peut ni les valider ni les supposer absentes.
		const missing = scopes
			? ["repo", "workflow"].filter((scope) => !scopes.includes(scope))
			: []
		if (missing.length > 0) {
			errors.push(
				`GITHUB_TOKEN : portée(s) manquante(s) — ${missing.join(", ")}. ` +
					"« repo » crée le dépôt et ses secrets, « workflow » autorise le push " +
					"de .github/workflows/.",
			)
		}

		const repoPresent = await input.github.repoExists(
			input.owner,
			input.repoName,
		)
		if (repoPresent && bootstrap) {
			errors.push(
				`Le repo ${input.owner}/${input.repoName} existe déjà — ` +
					`supprime-le (gh repo delete ${input.owner}/${input.repoName}), change WPR_REPO_NAME, ` +
					"ou retire WPR_BOOTSTRAP s'il s'agit d'ajouter un environnement.",
			)
		}
		if (!repoPresent && !bootstrap) {
			errors.push(
				`Le repo ${input.owner}/${input.repoName} est introuvable — passe WPR_BOOTSTRAP=true ` +
					"s'il s'agit du premier run du projet.",
			)
		}
	} catch (error) {
		errors.push(`GITHUB_TOKEN inutilisable : ${message(error)}`)
	}

	try {
		const project = await input.coolify.findProject(input.projectName)
		if (project && bootstrap) {
			errors.push(
				`Un projet Coolify « ${input.projectName} » existe déjà (${project}) — ` +
					"supprime-le depuis l'UI, ou retire WPR_BOOTSTRAP s'il s'agit d'y " +
					"ajouter un environnement.",
			)
		}
		if (!project && !bootstrap) {
			errors.push(
				`Aucun projet Coolify « ${input.projectName} » — passe WPR_BOOTSTRAP=true ` +
					"s'il s'agit du premier run du projet.",
			)
		}
		await input.coolify.resolveServer(input.coolifyServer)
	} catch (error) {
		errors.push(message(error))
	}

	if (input.cloudflare) {
		try {
			await input.cloudflare.zoneName()
		} catch (error) {
			errors.push(`CF_API_TOKEN / CF_ZONE_ID : ${message(error)}`)
		}
	}

	return errors
}

/**
 * Écrit `pnpm-lock.yaml` dans le repo généré, avant le commit de bootstrap.
 *
 * Sans lui, la CI du client échoue à sa toute première étape : `actions/setup-node`
 * refuse `cache: pnpm` sans lockfile, et `pnpm install --frozen-lockfile` échouerait
 * juste derrière. Le résoudre ici est ce qui permet de garder la CI stricte et les
 * installs reproductibles, plutôt que de relâcher les deux.
 *
 * Le jeton du registre ne passe PAS par le `.npmrc` du repo : ce fichier part dans
 * le commit livré au client. Il vit le temps de la commande dans un userconfig hors
 * du dépôt, en 600, supprimé ensuite — `NPM_CONFIG_USERCONFIG` prend le pas sur le
 * `~/.npmrc` de l'opérateur, ce qui rend l'étape indépendante de sa machine.
 */
function writeLockfile(targetDir: string, nodeAuthToken: string): void {
	const userconfig = join(tmpdir(), `wpr-npmrc-${randomUUID()}`)
	writeFileSync(
		userconfig,
		`//npm.pkg.github.com/:_authToken=${nodeAuthToken}\n`,
		{ mode: 0o600 },
	)

	try {
		execFileSync("pnpm", ["install", "--lockfile-only"], {
			cwd: targetDir,
			stdio: "inherit",
			env: { ...process.env, NPM_CONFIG_USERCONFIG: userconfig },
		})
	} catch (error) {
		throw new Error(
			"Échec de la génération du pnpm-lock.yaml. Vérifie que pnpm est installé " +
				"et que NODE_AUTH_TOKEN porte bien read:packages sur les @wp-reactor/*.\n" +
				(error instanceof Error ? error.message : String(error)),
		)
	} finally {
		rmSync(userconfig, { force: true })
	}
}

/**
 * Réécrit le `.env` de DEV local avec un `SESSION_SECRET` réel.
 *
 * Le template livre `SESSION_SECRET=change-me`, sous le plancher de 32
 * caractères que la coque vérifie au démarrage — `pnpm dev` échoue donc sur un
 * repo fraîchement généré. Ce fichier est gitignoré : il ne contient que des
 * identifiants de la stack Docker locale, jamais les secrets de prod, qui ne
 * transitent que par l'API Coolify.
 */
function writeLocalEnv(
	targetDir: string,
	sessionSecret: string,
): string | undefined {
	const example = join(targetDir, ".env.local.example")
	if (!existsSync(example)) return undefined

	const content = readFileSync(example, "utf8").replace(
		/^SESSION_SECRET=.*$/m,
		`SESSION_SECRET=${sessionSecret}`,
	)
	const dest = join(targetDir, ".env")
	writeFileSync(dest, content)
	return dest
}

/** Variables d'environnement de la ressource Compose WordPress. */
function wordpressEnvs(input: {
	image: string
	wpHost: string
	appHost: string
	dbHost: string
	redisHost: string
	/** `production` ou `staging` — valeurs reconnues par WordPress. */
	environmentType: string
	siteTitle: string
	adminEmail: string
	secrets: GeneratedSecrets
}) {
	return [
		{ key: "WORDPRESS_IMAGE", value: input.image },
		{ key: "WP_HOST", value: input.wpHost },
		{ key: "APP_HOST", value: input.appHost },
		{ key: "WORDPRESS_DB_HOST", value: input.dbHost },
		{ key: "WORDPRESS_DB_NAME", value: "wordpress" },
		{ key: "WORDPRESS_DB_USER", value: "wordpress" },
		{ key: "WORDPRESS_DB_PASSWORD", value: input.secrets.dbPassword },
		// WP_HOME est défini comme constante WordPress telle quelle
		// (`wordpress/docker/wp-reactor-config.php`) : WordPress exige une URL
		// complète, schéma compris.
		{ key: "WP_HOME", value: `https://${input.wpHost}` },
		{ key: "WP_SITEURL", value: `https://${input.wpHost}` },
		{ key: "FRONTEND_URL", value: `https://${input.appHost}` },
		{ key: "WP_REDIS_HOST", value: input.redisHost },
		{ key: "WP_REDIS_PASSWORD", value: input.secrets.redisPassword },
		// Webhook de purge du cache HTML de la webapp (plugin de base
		// `html-cache-purge.php`). L'URL vient de FRONTEND_URL ci-dessus.
		{ key: "CACHE_PURGE_SECRET", value: input.secrets.purgeSecret },
		// Valeur lue par `wp_get_environment_type()`, dont dépendent des plugins et
		// le comportement du core. « staging » est l'une des quatre valeurs que
		// WordPress reconnaît — la figer à « production » mentait sur l'environnement.
		{ key: "WP_ENVIRONMENT_TYPE", value: input.environmentType },
		{ key: "WP_DEBUG", value: "false" },
		// Provisionne WordPress au premier boot : supprime le
		// `docker exec … bootstrap.sh` manuel de docs/deploy.md.
		{ key: "WP_AUTO_BOOTSTRAP", value: "1" },
		{ key: "WP_SITE_TITLE", value: input.siteTitle },
		{ key: "WP_ADMIN_USER", value: "admin" },
		{ key: "WP_ADMIN_PASSWORD", value: input.secrets.wpAdminPassword },
		{ key: "WP_ADMIN_EMAIL", value: input.adminEmail },
	]
}

/** Variables d'environnement de l'Application webapp. */
function webappEnvs(input: {
	slug: string
	appHost: string
	redisHost: string
	secrets: GeneratedSecrets
	cloudflareZoneId?: string
	cloudflareToken?: string
}) {
	const envs = [
		{ key: "SESSION_SECRET", value: input.secrets.sessionSecret },
		{
			key: "WORDPRESS_INTERNAL_URL",
			value: `http://${input.slug}-wordpress-internal`,
		},
		{ key: "NODE_ENV", value: "production" },
		{ key: "APP_HOST", value: input.appHost },
		{ key: "PORT", value: "3000" },
		// Cache HTML côté origine : la MÊME instance Redis que l'object cache
		// WordPress, isolée par le préfixe de clé (`src/server/htmlCache.ts`).
		// C'est lui qui sert le HTML périmé pendant qu'un WordPress redémarre —
		// la seule protection contre une 502 quand le site n'a pas de CDN.
		{
			key: "REDIS_URL",
			value: `redis://:${input.secrets.redisPassword}@${input.redisHost}:6379`,
		},
		{ key: "CACHE_PURGE_SECRET", value: input.secrets.purgeSecret },
	]
	// `clear-cache.mjs` sort silencieusement si l'une des deux manque : on ne
	// pose la paire que si elle est complète.
	if (input.cloudflareZoneId && input.cloudflareToken) {
		envs.push({ key: "CF_ZONE_ID", value: input.cloudflareZoneId })
		envs.push({ key: "CF_API_TOKEN", value: input.cloudflareToken })
	}
	return envs
}

export async function provision(
	opts: ProvisionOptions,
): Promise<ProvisionResult> {
	const errors = preflight(opts)
	if (errors.length > 0) {
		throw new Error(`Préflight échoué :\n  - ${errors.join("\n  - ")}`)
	}

	const dryRun = opts.dryRun ?? false
	const log = opts.log ?? ((line: string) => console.log(line))
	const slug = toProjectSlug(opts.projectName)
	const targetDir = resolve(opts.targetDir)
	const repoName = opts.repoName ?? slug
	const appHost = opts.domain
	const wpHost = opts.wpHost ?? `wp.${opts.domain}`
	const secrets = generateSecrets()
	// Un nom d'image Docker n'accepte que des minuscules — GHCR comme la regex
	// de validation de Coolify. Un owner ou un repo capitalisé (« Growth-Angels »)
	// produisait une référence que Coolify refuse et que Docker ne saurait pas
	// tirer. La CI applique déjà le même abaissement de casse.
	const ghcrBase = `ghcr.io/${opts.githubOwner}/${repoName}`.toLowerCase()

	// Deux axes indépendants. L'ENVIRONNEMENT décide des noms et de la branche ;
	// le MODE décide de ce qui est créé. N'importe quel environnement peut être
	// le premier — l'ordre prod/staging n'est pas imposé.
	const staging = (opts.targetEnv ?? "production") === "staging"
	const bootstrap = opts.bootstrap === true
	// `stack` préfixe les ressources Coolify ET les identifiants Traefik du
	// compose : sans lui, les deux environnements se disputeraient les mêmes noms
	// de routeurs sur le même hôte Traefik.
	const stack = staging ? `${slug}-staging` : slug
	const coolifyEnvName = staging ? "development" : "production"
	const branch = staging ? "development" : "main"
	const githubEnv = staging ? "staging" : "production"
	const totalSteps = bootstrap ? 7 : 6
	// Les jetons FOURNIS sont masqués au même titre que les secrets générés :
	// certains transitent en clair dans un corps de requête (CF_API_TOKEN devient
	// une variable d'env Coolify), et la sortie d'un dry-run finit volontiers
	// collée dans un ticket. `coolifyUrl` en est exclu : ce n'est pas un secret,
	// et le masquer rendrait toutes les URL du journal illisibles.
	const masked = [
		...secretValues(secrets),
		opts.tokens.github,
		opts.tokens.coolifyToken,
		opts.tokens.nodeAuthToken,
		opts.tokens.cloudflareToken ?? "",
	].filter((value) => value.length > 0)

	const calls: HttpCall[] = []
	let step = 0
	const requester = new Requester({
		dryRun,
		fetchImpl: opts.fetchImpl,
		onCall: (call, simulated) => {
			calls.push(call)
			step += 1
			const prefix = simulated ? "[dry-run]" : "        →"
			log(`${prefix} ${step}. ${call.label}`)
			log(`${prefix}    ${call.method} ${redact(call.url, masked)}`)
			if (call.body !== undefined) {
				log(`${prefix}    ${redact(JSON.stringify(call.body), masked)}`)
			}
		},
	})

	const coolify = new CoolifyClient(
		opts.tokens.coolifyUrl,
		opts.tokens.coolifyToken,
		requester,
	)
	const github = new GitHubClient(opts.tokens.github, requester)
	const cloudflare =
		opts.tokens.cloudflareToken && opts.tokens.cloudflareZoneId
			? new CloudflareClient(
					opts.tokens.cloudflareZoneId,
					opts.tokens.cloudflareToken,
					requester,
				)
			: undefined

	// ── 0. Vérifications distantes ────────────────────────────────────────────
	// Un dry-run n'émet aucune requête : il ne peut donc rien sonder.
	if (!dryRun) {
		log(`\n▸ 0/${totalSteps} Vérifications préalables`)
		const remoteErrors = await preflightRemote({
			github,
			coolify,
			cloudflare,
			owner: opts.githubOwner,
			repoName,
			projectName: slug,
			coolifyServer: opts.coolifyServer,
			bootstrap,
		})
		if (remoteErrors.length > 0) {
			throw new Error(
				`Vérifications préalables échouées — rien n'a été créé :\n  - ${remoteErrors.join("\n  - ")}`,
			)
		}
	}

	// ── 1. Scaffolding ────────────────────────────────────────────────────────
	// Une greffe se pose sur un repo déjà livré : elle ne régénère rien, et lira
	// le compose du checkout que le préflight a exigé.
	let stepNo = 1
	if (bootstrap) {
		log(
			`\n▸ ${stepNo}/${totalSteps} Génération du repo client dans ${targetDir}`,
		)
		if (!dryRun) {
			scaffold({
				projectName: opts.projectName,
				targetDir,
				namespace: opts.namespace,
			})
			const envPath = writeLocalEnv(targetDir, secrets.sessionSecret)
			if (envPath) log(`        → ${envPath} écrit (gitignoré, dev local)`)
			log("        → pnpm install --lockfile-only")
			writeLockfile(targetDir, opts.tokens.nodeAuthToken)
			log("        → pnpm-lock.yaml écrit (committé, CI reproductible)")
		}
		stepNo += 1
	}

	// ── 2. Repo GitHub + variables et secrets déjà connus ─────────────────────
	log(
		`\n▸ ${stepNo}/${totalSteps} ${
			bootstrap ? "Repo GitHub" : "Repo GitHub existant"
		}, environment « ${githubEnv} », variables et secrets`,
	)
	stepNo += 1
	const viewer = await github.viewerLogin()
	const isOrg = viewer.toLowerCase() !== opts.githubOwner.toLowerCase()
	if (bootstrap) {
		await github.createRepo({
			owner: opts.githubOwner,
			repo: repoName,
			description: `Repo client WP Reactor — ${opts.projectName}`,
			private: true,
			isOrg,
		})
	}

	// Deux portées, et le partage n'est pas une préférence de style : `ci.yml` ne
	// porte AUCUN `environment:`, donc un secret qui n'existerait que dans un
	// environment lui serait invisible et casserait chaque PR.
	const repoRef = { owner: opts.githubOwner, repo: repoName }
	const envRef = { ...repoRef, environment: githubEnv }
	await github.ensureEnvironment(repoRef, githubEnv)

	// Propres à l'environnement — le host WordPress en tête. Bakées dans le
	// bundle Vite au build, elles doivent exister AVANT le push.
	for (const [name, value] of [
		["VITE_WORDPRESS_URL", `https://${wpHost}`],
		["VITE_FRONTEND_URL", `https://${appHost}`],
		["VITE_GRAPHQL_PATH", "/graphql"],
		["VITE_FEATURE_EDITORIAL", "true"],
		["VITE_FEATURE_ECOMMERCE", "false"],
		["VITE_FEATURE_AUTH", "false"],
	]) {
		await github.setVariable(envRef, name, value)
	}
	// Partagés par tous les environnements, et NODE_AUTH_TOKEN est lu par `ci.yml`
	// qui ne cible aucun environment : ils restent au niveau repo. Réécrits à
	// chaque run, ce qui est sans effet mais rend chaque run autonome.
	for (const [name, value] of [
		["NODE_AUTH_TOKEN", opts.tokens.nodeAuthToken],
		["COOLIFY_URL", opts.tokens.coolifyUrl],
		["COOLIFY_TOKEN", opts.tokens.coolifyToken],
	]) {
		await github.setSecret(repoRef, name, value)
	}

	// ── 3. Serveur Coolify + DNS ──────────────────────────────────────────────
	log(`\n▸ ${stepNo}/${totalSteps} Serveur Coolify et enregistrements DNS`)
	stepNo += 1
	const server = await coolify.resolveServer(opts.coolifyServer)
	const dnsTarget = opts.serverIp ?? server.ip
	if (cloudflare) {
		if (!opts.dryRun && !isIPv4(dnsTarget)) {
			throw new Error(
				`Le serveur Coolify "${opts.coolifyServer}" rapporte "${dnsTarget}", ` +
					"qui n'est pas une IPv4 — c'est le cas du serveur « localhost » d'une " +
					"instance auto-hébergée. Renseigne WPR_SERVER_IP avec l'IPv4 publique " +
					"de la machine qui héberge Coolify.",
			)
		}
		// Front proxifié (cache edge du HTML SSR + purge post-deploy) ;
		// WordPress DNS-only, TLS terminé par Traefik.
		await cloudflare.upsertARecord({
			name: appHost,
			content: dnsTarget,
			proxied: true,
		})
		await cloudflare.upsertARecord({
			name: wpHost,
			content: dnsTarget,
			proxied: false,
		})
	} else {
		log("        → CF_API_TOKEN/CF_ZONE_ID absents : DNS à créer à la main.")
	}

	// ── 4. Projet, environment, bases ─────────────────────────────────────────
	log(
		`\n▸ ${stepNo}/${totalSteps} Projet Coolify (environment « ${coolifyEnvName} »), MariaDB et Redis`,
	)
	stepNo += 1
	// Une greffe rejoint le projet existant ; le préflight a déjà garanti sa
	// présence, le repli ne sert qu'au dry-run, qui ne sonde rien.
	const projectUuid = bootstrap
		? await coolify.createProject(slug, `WP Reactor — ${opts.projectName}`)
		: ((await coolify.findProject(slug)) ?? "dry-run-project-uuid")
	const environmentUuid = await coolify.resolveEnvironment(
		projectUuid,
		coolifyEnvName,
	)
	const mariadbUuid = await coolify.createMariadb({
		name: `${stack}-mariadb`,
		projectUuid,
		environmentUuid,
		environmentName: coolifyEnvName,
		serverUuid: server.uuid,
		database: "wordpress",
		user: "wordpress",
		password: secrets.dbPassword,
		rootPassword: secrets.dbRootPassword,
	})
	const redisUuid = await coolify.createRedis({
		name: `${stack}-redis`,
		projectUuid,
		environmentUuid,
		environmentName: coolifyEnvName,
		serverUuid: server.uuid,
		password: secrets.redisPassword,
	})

	// ── 5. Ressource Compose WordPress ────────────────────────────────────────
	log(`\n▸ ${stepNo}/${totalSteps} Ressource Docker Compose WordPress`)
	stepNo += 1
	const composePath = join(targetDir, "apps/wordpress/docker-compose.yml")
	const composeRaw = dryRun
		? "# (compose non lu en dry-run : le scaffolding n'a pas eu lieu)"
		: readFileSync(composePath, "utf8")
	// Le compose est scaffoldé avec le slug EN DUR dans le nom du projet compose,
	// l'alias réseau et tous les identifiants Traefik (routers, services,
	// middlewares). Deux stacks sur le même hôte Traefik se disputeraient ces
	// noms : staging les repréfixe. Les hosts, eux, passent par ${WP_HOST} /
	// ${APP_HOST} et ne sont pas touchés.
	const composeForStack = staging
		? composeRaw.replaceAll(`${slug}-`, `${stack}-`)
		: composeRaw
	const wordpressUuid = await coolify.createService({
		name: `${stack}-wordpress`,
		description: "WordPress headless (image GHCR construite en CI)",
		projectUuid,
		environmentUuid,
		environmentName: coolifyEnvName,
		serverUuid: server.uuid,
		dockerComposeRaw: composeForStack,
	})
	await coolify.setEnvs(
		"services",
		wordpressUuid,
		wordpressEnvs({
			// Le tag suit la branche de l'environnement : la CI de staging pousse
			// `:development`, celle de production `:main`. Figer `:main` faisait
			// tirer à staging une image qui n'existe pas pour lui.
			image: `${ghcrBase}/wordpress:${branch}`,
			wpHost,
			appHost,
			// Hostname interne d'une base managée = son UUID (cf. coolify.ts).
			dbHost: internalHostFor(mariadbUuid),
			redisHost: internalHostFor(redisUuid),
			environmentType: staging ? "staging" : "production",
			siteTitle: opts.projectName,
			adminEmail: `admin@${opts.domain}`,
			secrets,
		}),
	)

	// ── 6. Application webapp ─────────────────────────────────────────────────
	log(`\n▸ ${stepNo}/${totalSteps} Application webapp`)
	stepNo += 1
	const webappUuid = await coolify.createDockerImageApplication({
		name: `${stack}-webapp`,
		description: "Front SSR TanStack Start",
		projectUuid,
		environmentUuid,
		environmentName: coolifyEnvName,
		serverUuid: server.uuid,
		image: `${ghcrBase}/webapp`,
		imageTag: branch,
		domains: `https://${appHost}`,
		portsExposes: "3000",
		healthCheckPath: "/api/health",
		postDeploymentCommand: "node scripts/clear-cache.mjs",
	})
	// Second appel obligatoire : Coolify écrase `custom_labels` à la création.
	await coolify.setApplicationLabels(
		webappUuid,
		webappTraefikLabels(stack, appHost),
	)
	await coolify.setEnvs(
		"applications",
		webappUuid,
		webappEnvs({
			// Alimente WORDPRESS_INTERNAL_URL, qui vise l'alias réseau du compose —
			// repréfixé lui aussi pour staging, sinon le front SSR taperait sur le
			// WordPress de production.
			slug: stack,
			appHost,
			redisHost: internalHostFor(redisUuid),
			secrets,
			cloudflareZoneId: opts.tokens.cloudflareZoneId,
			cloudflareToken: opts.tokens.cloudflareToken,
		}),
	)

	// ── 7. UUID Coolify côté GitHub, puis push ────────────────────────────────
	log(
		`\n▸ ${stepNo}/${totalSteps} Secrets COOLIFY_*_UUID et push sur « ${branch} »`,
	)
	// Scopés : ce sont EUX qui pointaient sur les ressources de production depuis
	// n'importe quelle branche. Chaque environnement porte désormais les siens.
	await github.setSecret(envRef, "COOLIFY_WORDPRESS_UUID", wordpressUuid)
	await github.setSecret(envRef, "COOLIFY_WEBAPP_UUID", webappUuid)

	pushInitialCommit({
		dir: targetDir,
		cloneUrl: `https://github.com/${opts.githubOwner}/${repoName}.git`,
		branch,
		token: opts.tokens.github,
		message:
			"chore: bootstrap du repo client via create-wp-reactor --provision",
		bootstrap,
		dryRun,
		log: (line) => log(`        → ${line}`),
	})

	return {
		slug,
		targetDir,
		appHost,
		wpHost,
		repoUrl: `https://github.com/${opts.githubOwner}/${repoName}`,
		actionsUrl: `https://github.com/${opts.githubOwner}/${repoName}/actions`,
		wpAdminPassword: secrets.wpAdminPassword,
		coolify: {
			projectUuid,
			environmentUuid,
			mariadbUuid,
			redisUuid,
			wordpressUuid,
			webappUuid,
		},
		calls,
	}
}

/** Récapitulatif de fin de course, affiché par la CLI. */
export function summary(result: ProvisionResult): string {
	return [
		"",
		"✓ Infra provisionnée.",
		"",
		`  Repo        ${result.repoUrl}`,
		`  CI          ${result.actionsUrl}`,
		`  Front       https://${result.appHost}`,
		`  WordPress   https://${result.wpHost}/wp-admin`,
		"",
		"  Admin WordPress : admin",
		`  Mot de passe    : ${result.wpAdminPassword}`,
		"  (à noter maintenant — il n'est stocké que dans l'env Coolify)",
		"",
		"Le premier run de la CI construit les images et déclenche les déploiements.",
		"",
		"⚠ Prérequis restant, non automatisable : l'hôte Coolify doit pouvoir tirer",
		"  depuis GHCR. L'API Coolify n'expose pas les credentials de registry.",
		"  Sur l'hôte, en root :  sudo docker login ghcr.io",
	].join("\n")
}
